/**
 * AcceptInvitationPage Component
 *
 * Public page for accepting user invitations.
 * Users click a link in their invitation email and land here to set up their account.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Alert } from '../../../components/ui/Alert';
import { Card } from '../../../components/ui/Card';
import { useAcceptInvitation } from '../hooks/useAcceptInvitation';
import { getErrorMessage } from '../../../lib/utils/errors';
import { nameSchema, passwordSchema } from '../../../lib/validators/common';

// Validation schema
const acceptInvitationSchema = z.object({
  invitationToken: z.string().min(1, 'Invitation token is required'),
  firstName: nameSchema,
  lastName: nameSchema,
  password: passwordSchema,
  confirmPassword: z.string().min(1, 'Please confirm your password'),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ['confirmPassword'],
});

type AcceptInvitationFormData = z.infer<typeof acceptInvitationSchema>;

export const AcceptInvitationPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tenantId, setTenantId] = useState<string>('');
  const [invitationToken, setInvitationToken] = useState<string>('');

  const acceptInvitationMutation = useAcceptInvitation(tenantId);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
  } = useForm<AcceptInvitationFormData>({
    resolver: zodResolver(acceptInvitationSchema),
  });

  // Extract tenant ID and token from URL
  useEffect(() => {
    const tenant = searchParams.get('tenantId') || '';
    const token = searchParams.get('token') || '';

    setTenantId(tenant);
    setInvitationToken(token);
    setValue('invitationToken', token);

    if (!tenant || !token) {
      console.error('Missing tenantId or token in URL');
    }
  }, [searchParams, setValue]);

  const onSubmit = async (data: AcceptInvitationFormData) => {
    if (!tenantId) {
      console.error('Tenant ID is missing');
      return;
    }

    try {
      await acceptInvitationMutation.mutateAsync({
        invitationToken: data.invitationToken,
        firstName: data.firstName,
        lastName: data.lastName,
        password: data.password,
      });

      // Success - redirect to login
      navigate('/login', {
        state: {
          message: 'Account created successfully! Please log in.',
        },
      });
    } catch (error) {
      console.error('Failed to accept invitation:', error);
    }
  };

  // Show error if missing parameters
  if (!tenantId || !invitationToken) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: 'var(--color-bg-primary)' }}>
        <Card className="max-w-md w-full">
          <Card.Header>
            <Card.Title>Invalid Invitation Link</Card.Title>
          </Card.Header>
          <Card.Body>
            <Alert variant="error">
              This invitation link is invalid or has expired. Please contact your
              administrator for a new invitation.
            </Alert>
          </Card.Body>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: 'var(--color-bg-primary)' }}>
      <Card className="max-w-md w-full">
        <Card.Header>
          <Card.Title>Accept Invitation</Card.Title>
          <p className="mt-1 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
            Set up your account to get started
          </p>
        </Card.Header>

        <Card.Body>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {acceptInvitationMutation.isError && (
              <Alert variant="error">
                {getErrorMessage(acceptInvitationMutation.error)}
              </Alert>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="firstName"
                  className="block text-sm font-medium mb-1"
                  style={{ color: 'var(--color-text-secondary)' }}
                >
                  First Name <span className="text-red-500">*</span>
                </label>
                <Input
                  id="firstName"
                  type="text"
                  {...register('firstName')}
                  error={errors.firstName?.message}
                  placeholder="John"
                  disabled={isSubmitting}
                />
                {errors.firstName && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.firstName.message}
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="lastName"
                  className="block text-sm font-medium mb-1"
                  style={{ color: 'var(--color-text-secondary)' }}
                >
                  Last Name <span className="text-red-500">*</span>
                </label>
                <Input
                  id="lastName"
                  type="text"
                  {...register('lastName')}
                  error={errors.lastName?.message}
                  placeholder="Doe"
                  disabled={isSubmitting}
                />
                {errors.lastName && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.lastName.message}
                  </p>
                )}
              </div>
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium mb-1"
                style={{ color: 'var(--color-text-secondary)' }}
              >
                Password <span className="text-red-500">*</span>
              </label>
              <Input
                id="password"
                type="password"
                {...register('password')}
                error={errors.password?.message}
                placeholder="--------"
                disabled={isSubmitting}
              />
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.password.message}
                </p>
              )}
              <p className="mt-1 text-xs" style={{ color: 'var(--color-text-muted)' }}>
                Must be at least 8 characters with uppercase, lowercase, number, and special character
              </p>
            </div>

            <div>
              <label
                htmlFor="confirmPassword"
                className="block text-sm font-medium mb-1"
                style={{ color: 'var(--color-text-secondary)' }}
              >
                Confirm Password <span className="text-red-500">*</span>
              </label>
              <Input
                id="confirmPassword"
                type="password"
                {...register('confirmPassword')}
                error={errors.confirmPassword?.message}
                placeholder="--------"
                disabled={isSubmitting}
              />
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.confirmPassword.message}
                </p>
              )}
            </div>

            <Button
              type="submit"
              variant="primary"
              className="w-full"
              loading={isSubmitting}
              disabled={isSubmitting}
            >
              Create Account
            </Button>
          </form>
        </Card.Body>
      </Card>
    </div>
  );
};

export default AcceptInvitationPage;
